/**
 * TODAY'S TREASURE — one honest deal, and it leaves once it has done its job.
 *
 * The old card was a permanent fixture: same item for everyone, still sitting
 * there after the family had added it. This one reports what happened to it, so
 * the server can stop offering it: `shown` the first time it is drawn, then
 * `added`, `dismissed` or `another`.
 *
 * THE CONFIRMATION IS THE CARD. When the family taps it, the card ITSELF turns
 * into "Added · Bananas" with the green check for exactly 2 seconds and then
 * collapses away — no second green bar stacked on top of it. One message, one
 * place.
 *
 * NO FAKE URGENCY: no countdown, no "ends in". The title says "Today's
 * treasure" when the pick rotates daily and "This week's treasure" only when
 * the grocer has actually set a weekly deal. A paid placement says so.
 * Reduced motion is respected — the collapse is a fade, never a slide.
 */
import { Check, Gift, Plus, RefreshCw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import GroceryIcon from './GroceryIcon';

export const ADDED_MS = 2000;      // exactly 2 seconds, everywhere

export default function TreasureCard({ treasure, onAdd, onEvent, onAnother, frost = '' }) {
    const { t } = useTranslation();
    const [phase, setPhase] = useState('idle');   // idle | added | gone
    // THE CARD HOLDS ITS OWN COPY while it is saying "Added". The list refreshes
    // the moment an item lands, and the server stops offering a treasure once it
    // has been added — so reading straight from the prop made the confirmation
    // vanish after 300 ms instead of 2 seconds.
    const [card, setCard] = useState(treasure);
    const shownFor = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        if (phase !== 'idle') return;
        if ((treasure?.id || null) === (card?.id || null)) return;
        setCard(treasure);
        if (treasure?.id && shownFor.current !== treasure.id) {
            shownFor.current = treasure.id;
            onEvent?.(treasure.id, 'shown');
        }
    }, [treasure, card, phase]);        // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (treasure?.id && shownFor.current === null) {
            shownFor.current = treasure.id;
            onEvent?.(treasure.id, 'shown');
        }
    }, [treasure?.id]);                 // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => clearTimeout(timer.current), []);

    if (!card || !card.name || phase === 'gone') return null;

    const tap = async () => {
        if (phase !== 'idle') return;
        setPhase('added');
        await onAdd({ name: card.name, catalog_id: card.catalog_id });
        onEvent?.(card.id, 'added');
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setPhase('gone'), ADDED_MS);
    };

    const dismiss = (e) => {
        e.stopPropagation();
        onEvent?.(card.id, 'dismissed');
        setPhase('gone');
    };

    const another = (e) => {
        e.stopPropagation();
        onEvent?.(card.id, 'another');
        onAnother?.();
    };

    if (phase === 'added') {
        return (
            <div data-testid="grocery-treasure-added"
                className={`flex w-full items-center gap-3 rounded-2xl border-2 border-emerald-400 bg-emerald-50 px-4 py-3 text-left transition-opacity duration-200 dark:border-emerald-500/50 dark:bg-emerald-900/60${frost}`}>
                <Check className="h-6 w-6 shrink-0 text-emerald-600 dark:text-emerald-300" />
                <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                        {t('grocery.added')}
                    </span>
                    <span className="block break-words text-sm font-semibold leading-snug text-emerald-900 dark:text-emerald-100">
                        {card.name}
                    </span>
                </span>
            </div>
        );
    }

    return (
        <div data-testid="grocery-treasure-wrap"
            className={`relative w-full rounded-2xl border-2 border-[#F58220] bg-[#13265C] text-white shadow-[0_0_22px_-6px_rgba(245,130,32,0.75)] transition-opacity duration-200 ${card.matches_favourite ? 'motion-safe:play-pulse' : ''}`}>
            <button onClick={dismiss} data-testid="grocery-treasure-dismiss"
                aria-label={t('grocery.treasureDismiss')}
                className="absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center rounded-full text-[#FFC08A] hover:bg-white/10">
                <X className="h-4 w-4" />
            </button>
            <button onClick={tap} data-testid="grocery-treasure"
                className="flex w-full items-center gap-3 px-4 py-3 pr-12 text-left">
                {card.icon
                    ? <GroceryIcon icon={card.icon} badge={card.badge} size={36} />
                    : <Gift className="h-6 w-6 text-[#F58220]" />}
                <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold uppercase tracking-wide text-[#FFC08A]">
                        {card.title_mode === 'weekly'
                            ? t('grocery.treasureTitle') : t('grocery.treasureTitleToday')}
                        {card.sample ? ` · ${t('play.sample')}` : ''}
                    </span>
                    <span className="block break-words text-sm font-semibold leading-snug text-white">
                        {card.name}
                        {card.matches_favourite ? ` · ${t('grocery.treasureFavourite')}` : ''}
                    </span>
                    {card.note ? (
                        <span className="block break-words text-xs leading-snug text-[#D7DEF2]"
                            data-testid="grocery-treasure-note">{card.note}</span>
                    ) : null}
                    {card.sponsored ? (
                        <span className="mt-1 inline-block rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#FFC08A]"
                            data-testid="grocery-treasure-paid">{t('grocery.treasurePaid')}</span>
                    ) : null}
                </span>
                <Plus className="h-5 w-5 shrink-0 text-[#F58220]" />
            </button>
            {card.another_left > 0 ? (
                <button onClick={another} data-testid="grocery-treasure-another"
                    className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-b-2xl border-t border-white/15 px-4 text-xs font-bold text-[#FFC08A] hover:bg-white/10">
                    <RefreshCw className="h-3.5 w-3.5" /> {t('grocery.treasureAnother')}
                </button>
            ) : null}
        </div>
    );
}
